import { useState, useEffect } from 'react';
import { Modal, Table, Tag, Space, Button, Alert, Tooltip, Progress, message, Select, InputNumber } from '@basic-components';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, QuestionCircleOutlined, SyncOutlined, SettingOutlined } from '@ant-design/icons';
import { useMutationFormData } from "@hooks/useRequest";
import { ImportResultModal } from './ImportResultModal';

interface MappingConfirmModalProps {
  visible: boolean;
  file: File | null;
  importUrl: string;
  onCancel: () => void;
  onConfirm: () => void;
  onSuccess: () => void;
}

interface HeaderInfo {
  headers: string[];
  sampleRows: Record<string, any>[];
  totalColumns: number;
  totalDataRows: number;
  errorMessage: string | null;
  headerRowIndex: number;
  firstDataRow: Record<string, any>;
}

interface TemplateConfig {
  headerRowIndex: number;
  dataStartRow: number;
  entityClass: string | null;
  columnMappings: Record<string, string>;
  standardFields: any | null;
  dataMappings: Record<string, Record<string, string>>;
  dataMappingCaseSensitive: boolean;
  requiredFields: string[];
  validators: Record<string, string>;
  maxLengths: Record<string, number>;
  uniqueFields: string[][];
  dataSourceValidators: Record<string, any>;
}

interface TemplateMetadata {
  id: string;
  sort: number;
  remark: string | null;
  createTime: string | null;
  createBy: number;
  updateTime: string | null;
  updateBy: number;
  deleted: boolean;
  version: number;
  businessType: string;
  templateName: string;
  templateCode: string;
  description: string;
  priority: number;
  config: TemplateConfig;
  status: number;
  isDefault: boolean;
}

interface MappingDetail {
  column: string;
  field: string;
  matched: boolean;
  source: string;
}

interface PreviewResult {
  fileName: string;
  headerInfo: HeaderInfo;
  totalColumns: number;
  templateMetadata: TemplateMetadata | null;
  mappings: MappingDetail[];
  templateName: string;
  columns: number;
  unmappedColumns: string[];
  needAiAnalysis?: boolean;
  mappingRules?: any;
}

interface ImportResultData {
  successCount: number;
  failCount: number;
  templateName: string;
  aiAnalysisUsed: boolean;
  errorDetails?: string[];
}

// 可选的字段列表
const AVAILABLE_FIELDS = [
  { value: 'exhibitorName', label: '所属公司' },
  { value: 'name', label: '姓名' },
  { value: 'surname', label: '姓(拼音)' },
  { value: 'givenName', label: '名(拼音)' },
  { value: 'gender', label: '性别' },
  { value: 'birthday', label: '出生日期' },
  { value: 'phone', label: '手机号码' },
  { value: 'email', label: '邮箱' },
  { value: 'position', label: '职务' },
  { value: 'idType', label: '证件类型' },
  { value: 'idNo', label: '身份证号' },
  { value: 'passport', label: '护照号' },
  { value: 'remark', label: '备注' },
  { value: '', label: '不导入' },
];

// 构造映射详情
function buildMappings(headers: string[], columnMappings: Record<string, string> | undefined) {
  return headers.map(header => {
    const field = columnMappings?.[header] || '';
    return {
      column: header,
      field,
      matched: !!field,
      source: field ? '模板' : '未匹配',
    };
  });
}

export const MappingConfirmModal = ({
  visible,
  file,
  importUrl,
  onCancel,
  onConfirm,
  onSuccess,
}: MappingConfirmModalProps) => {
  const [previewData, setPreviewData] = useState<PreviewResult | null>(null);
  const [userMappings, setUserMappings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  // 导入结果弹窗相关状态
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [importResult, setImportResultData] = useState<ImportResultData | null>(null);
  // 新增：headerRowIndex 和 dataStartRow 的状态（内部存储 0-based index，用于与后端交互）
  const [headerRowIndex, setHeaderRowIndex] = useState<number>(0);
  const [dataStartRow, setDataStartRow] = useState<number>(1);
  const [templateCode, setTemplateCode] = useState<string>('');

  // 使用 useMutationFormData 调用导入
  const params = new URLSearchParams();
  params.append("importUrl", importUrl || '');
  const queryString = params.toString();
  const { mutateAsync: uploadAsync } = useMutationFormData(`/import-excel?${ queryString }`);

  // 通用预览映射结果（不触发AI）
  const handlePreview = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('useAi', 'false');

      const response = await fetch('/api/previewImportMapping', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const res = await response.json();
      if (res.code === 200) {
        // 适配新结构
        const headers = res.data.headerInfo?.headers || [];
        const columnMappings = res.data.templateMetadata?.config?.columnMappings || {};
        const mappings = buildMappings(headers, columnMappings);
        const unmappedColumns = headers.filter((h: string) => !columnMappings[h]);
        setPreviewData({
          ...res.data,
          mappings,
          templateName: res.data.templateMetadata?.templateName || '未知模板',
          columns: res.data.totalColumns || headers.length,
          unmappedColumns,
        });
        // 初始化用户映射
        const initialMappings: Record<string, string> = {};
        mappings.forEach((m: any) => {
          initialMappings[m.column] = m.field || '';
        });
        setUserMappings(initialMappings);
        // 初始化行配置（后端返回的是 0-based index，内部存储原始值）
        const config = res.data.templateMetadata?.config;
        setHeaderRowIndex(config?.headerRowIndex ?? 0);
        setDataStartRow(config?.dataStartRow ?? 1);
        setTemplateCode(res.data.templateMetadata?.templateCode || '');
      } else {
        message.error(res.message || '预览失败');
      }
    } catch (error) {
      console.error('预览映射失败:', error);
      message.error('预览映射失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // AI 分析映射
  const handleAiAnalysis = async () => {
    if (!file) return;

    setAiLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('useAi', 'true');

      const response = await fetch('/api/previewImportMapping', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const res = await response.json();
      if (res.code === 200) {
        // 适配新结构
        const headers = res.data.headerInfo?.headers || [];
        const columnMappings = res.data.templateMetadata?.config?.columnMappings || {};
        const mappings = buildMappings(headers, columnMappings);
        const unmappedColumns = headers.filter((h: string) => !columnMappings[h]);
        setPreviewData({
          ...res.data,
          mappings,
          templateName: res.data.templateMetadata?.templateName || '未知模板',
          columns: res.data.totalColumns || headers.length,
          unmappedColumns,
        });
        // 初始化用户映射
        const initialMappings: Record<string, string> = {};
        mappings.forEach((m: any) => {
          initialMappings[m.column] = m.field || '';
        });
        setUserMappings(initialMappings);
        // 初始化行配置（后端返回的是 0-based index，内部存储原始值）
        const config = res.data.templateMetadata?.config;
        setHeaderRowIndex(config?.headerRowIndex ?? 0);
        setDataStartRow(config?.dataStartRow ?? 1);
        setTemplateCode(res.data.templateMetadata?.templateCode || '');
        message.success('AI 分析完成');
      } else {
        message.error(res.message || 'AI 分析失败');
      }
    } catch (error) {
      console.error('AI 分析失败:', error);
      message.error('AI 分析失败，请重试');
    } finally {
      setAiLoading(false);
    }
  };

  // 处理映射字段变更
  const handleMappingChange = (column: string, newField: string) => {
    setUserMappings(prev => ({
      ...prev,
      [column]: newField,
    }));
  };

  // 确认导入
  const handleConfirmImport = async () => {
    if (!file) return;

    setConfirming(true);
    setImportProgress(0);

    try {
      const progressTimer = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressTimer);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const formData = new FormData();
      formData.append('file', file);

      // 构建 columnMappings：只包含用户选择的有效映射（排除空值和"不导入"选项）
      const columnMappings: Record<string, string> = {};
      Object.entries(userMappings).forEach(([column, field]) => {
        if (field && field.length > 0) {
          columnMappings[column] = field;
        }
      });

      // 构建 templateMetadata JSON 字符串（符合后端 ImportTemplateMetadata 结构）
      const templateMetadata = {
        businessType: previewData?.templateMetadata?.businessType || 'exhibitor_staff',
        templateName: previewData?.templateMetadata?.templateName || '未知模板',
        templateCode: templateCode || '',
        config: {
          headerRowIndex: headerRowIndex,
          dataStartRow: dataStartRow,
          columnMappings: columnMappings,
        },
      };
      formData.append('templateMetadata', JSON.stringify(templateMetadata));

      clearInterval(progressTimer);
      setImportProgress(100);

      const res = await uploadAsync(formData);

      // 判断是否成功：code === 200 或 success === true
      const isSuccess = res?.code === 200 || res?.success === true;

      // 构建导入结果数据
      const resultData: ImportResultData = {
        successCount: res?.data?.successCount ?? res?.successCount ?? 0,
        failCount: res?.data?.failCount ?? res?.failCount ?? 0,
        templateName: res?.data?.templateName || res?.templateName || previewData?.templateName || '未知模板',
        aiAnalysisUsed: res?.data?.aiAnalysisUsed ?? res?.aiAnalysisUsed ?? false,
        errorDetails: res?.data?.errorDetails ?? res?.errorDetails ?? [],
      };

      if (isSuccess) {
        // 显示导入结果弹窗
        setImportResultData(resultData);
        setResultModalVisible(true);
        // 不立即关闭当前弹窗，等用户查看结果后再处理
      } else {
        message.error(res?.message || res?.data?.message || '导入失败');
        setImportProgress(0);
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请重试');
      setImportProgress(0);
    } finally {
      setConfirming(false);
    }
  };

  // 处理导入结果弹窗关闭（只关闭结果弹窗，回到导入确认页面）
  const handleResultModalOnlyClose = () => {
    setResultModalVisible(false);
    setImportResultData(null);
  };

  // 处理查看列表（关闭结果弹窗 + 导入确认页面 + 刷新列表）
  const handleViewList = () => {
    setResultModalVisible(false);
    setImportResultData(null);
    onSuccess?.();
    handleClose();
  };

  // 关闭并重置
  const handleClose = () => {
    setPreviewData(null);
    setUserMappings({});
    setImportProgress(0);
    setHeaderRowIndex(0);
    setDataStartRow(1);
    setTemplateCode('');
    onCancel();
  };

  // 监听 visible 变化，自动预览（通用分析）
  useEffect(() => {
    if (visible && file) {
      handlePreview();
    }
  }, [visible, file]);

  // 获取匹配状态图标
  // Remove unused 'matched' param
  const getMatchIcon = (source: string) => {
    if (source === '模板') {
      return <CheckCircleOutlined className="text-green-500" />;
    }
    if (source === 'AI') {
      return <ExclamationCircleOutlined className="text-orange-500" />;
    }
    return <CloseCircleOutlined className="text-red-400" />;
  };

  // 判断映射是否被用户修改
  const isMappingModified = (column: string, originalField: string): boolean => {
    return userMappings[column] !== (originalField || '');
  };

  // 判断映射是否有效（已选择字段且不为空）
  const isMappingValid = (column: string): boolean => {
    const field = userMappings[column];
    return Boolean(field && field.length > 0);
  };

  const columns = [
    {
      title: 'Excel 列名',
      dataIndex: 'column',
      key: 'column',
      width: '30%',
      render: (column: string, record: MappingDetail) => (
        <span className={!record.matched ? 'text-red-600 font-semibold' : ''}>
          {column}
        </span>
      ),
    },
    {
      title: '映射字段',
      dataIndex: 'field',
      key: 'field',
      width: '40%',
      render: (_: any, record: MappingDetail) => (
        <Select
          value={userMappings[record.column] || ''}
          onChange={(value) => handleMappingChange(record.column, value)}
          style={{ width: '100%' }}
          size="small"
          status={!isMappingValid(record.column) ? 'error' : isMappingModified(record.column, record.field) ? 'warning' : undefined}
          placeholder="选择映射字段"
        >
          {AVAILABLE_FIELDS.map(f => (
            <Select.Option key={f.value} value={f.value}>
              {f.label}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: '15%',
      render: (source: string, record: MappingDetail) => (
        <Space>
          {getMatchIcon(source)}
          <span className={!record.matched ? 'text-red-500 font-medium' : ''}>{source}</span>
        </Space>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: '15%',
      render: (_: any, record: MappingDetail) => {
        const modified = isMappingModified(record.column, record.field);

        if (modified) {
          return (
            <Tooltip title="已手动调整">
              <Tag color="blue" className="cursor-pointer">已调整</Tag>
            </Tooltip>
          );
        }
        if (!record.matched) {
          return (
            <Tooltip title="需要人工映射">
              <Tag color="error" className="cursor-pointer">待处理</Tag>
            </Tooltip>
          );
        }
        if (record.source === 'AI') {
          return (
            <Tooltip title="AI 智能识别，建议确认映射是否正确">
              <QuestionCircleOutlined className="text-orange-400 cursor-help" />
            </Tooltip>
          );
        }
        return <CheckCircleOutlined className="text-green-500" />;
      },
    },
  ];

  // 表格行样式：未匹配的列显示红色背景
  const getRowClassName = (_record: any, index: number): string => {
    const record = previewData?.mappings?.[index];
    if (!record?.matched) {
      return 'bg-red-50 hover:bg-red-100'; // 未匹配行醒目红色背景
    }
    return '';
  };

  return (
    <>
    <Modal
      title="导入数据 - 字段映射确认"
      open={visible}
      onCancel={handleClose}
      width={900}
      footer={
        <Space>
          <Button onClick={handleClose} disabled={confirming}>
            取消
          </Button>
          <Button
            onClick={handleAiAnalysis}
            loading={aiLoading}
            disabled={confirming || loading}
            icon={<SyncOutlined spin={aiLoading} />}
          >
            AI 分析
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmImport}
            loading={confirming}
            icon={<CheckCircleOutlined />}
          >
            确认导入
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Progress type="circle" percent={75} status="active" />
          <p className="mt-4 text-gray-500">正在分析 Excel 文件...</p>
        </div>
      ) : previewData ? (
        <div className="space-y-4">
          {/* 文件信息 */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500">文件名称：</span>
                <span className="font-medium">{previewData.fileName}</span>
              </div>
              <div>
                <span className="text-gray-500">列数：</span>
                <span className="font-medium">{previewData.columns}</span>
              </div>
              <div>
                <span className="text-gray-500">使用模板：</span>
                <Tag color={previewData.templateName !== '未知模板' ? 'green' : 'orange'}>
                  {previewData.templateName}
                </Tag>
              </div>
              {/*<div>*/}
              {/*  <span className="text-gray-500">匹配率：</span>*/}
              {/*  <span className={`font-medium ${*/}
              {/*    previewData.matchRate >= 0.8 ? 'text-green-600' : */}
              {/*    previewData.matchRate >= 0.5 ? 'text-orange-600' : 'text-red-600'*/}
              {/*  }`}>*/}
              {/*    {(previewData.matchRate * 100).toFixed(0)}%*/}
              {/*  </span>*/}
              {/*</div>*/}
            </div>
          </div>

          {/* 行配置区域 */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-blue-800 flex items-center">
                <span className="w-1 h-4 bg-blue-500 mr-2"></span>
                行配置（可人工调整）
              </h4>
              <Tag color="processing">默认值来自模板配置</Tag>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">
                  列头行号
                  <Tooltip title="Excel 中包含列标题的行号（从1开始）">
                    <QuestionCircleOutlined className="ml-1 text-gray-400" />
                  </Tooltip>
                </label>
                <InputNumber
                  min={1}
                  max={101}
                  value={headerRowIndex + 1}
                  onChange={(value) => setHeaderRowIndex((value ?? 1) - 1)}
                  style={{ width: '100%' }}
                  status={(headerRowIndex + 1) === (previewData?.templateMetadata?.config?.headerRowIndex ?? 0) + 1 ? undefined : 'warning'}
                />
                <span className="text-xs text-gray-400 mt-1">
                  模板默认值: {(previewData?.templateMetadata?.config?.headerRowIndex ?? 0) + 1}
                </span>
              </div>
              <div className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">
                  数据起始行号
                  <Tooltip title="Excel 中第一行数据所在的行号（从1开始）">
                    <QuestionCircleOutlined className="ml-1 text-gray-400" />
                  </Tooltip>
                </label>
                <InputNumber
                  min={1}
                  max={101}
                  value={dataStartRow + 1}
                  onChange={(value) => setDataStartRow((value ?? 1) - 1)}
                  style={{ width: '100%' }}
                  status={(dataStartRow + 1) === (previewData?.templateMetadata?.config?.dataStartRow ?? 1) + 1 ? undefined : 'warning'}
                />
                <span className="text-xs text-gray-400 mt-1">
                  模板默认值: {(previewData?.templateMetadata?.config?.dataStartRow ?? 1) + 1}
                </span>
              </div>
{/*              <div className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">
                  模板代码
                  <Tooltip title="当前匹配的导入模板代码">
                    <QuestionCircleOutlined className="ml-1 text-gray-400" />
                  </Tooltip>
                </label>
                <InputNumber
                  disabled
                  value={templateCode ? 1 : 0}
                  style={{ width: '100%', display: 'none' }}
                />
                <div className="px-3 py-2 bg-white rounded border border-gray-200 text-sm">
                  {templateCode || '未匹配模板'}
                </div>
                <span className="text-xs text-gray-400 mt-1">
                  {templateCode ? '将传递到导入接口' : '将使用通用导入逻辑'}
                </span>
              </div>*/}
            </div>
          </div>

          {/* 警告提示 */}
          {previewData.needAiAnalysis && (
            <Alert
              title="需要使用 AI 智能识别"
              description="未匹配到已知模板，需要使用AI 智能分析进行识别"
              type="warning"
              showIcon
            />
          )}

          {previewData.unmappedColumns && previewData.unmappedColumns.length > 0 && (
            <Alert
              title="部分列未识别"
              description={`以下列未匹配到系统字段，将被忽略：${ previewData.unmappedColumns.join('、') }`}
              type="info"
              showIcon
            />
          )}

          {/* 映射详情表格 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">
                字段映射详情
                {previewData.mappings?.some((m: MappingDetail) => !m.matched) && (
                  <Tag color="error" className="ml-2">存在未匹配字段</Tag>
                )}
              </h4>
              <span className="text-xs text-gray-500">点击映射字段可手动调整</span>
            </div>
            <Table
              columns={columns}
              dataSource={previewData.mappings}
              rowKey="column"
              size="small"
              pagination={false}
              scroll={{ y: 300 }}
              rowClassName={getRowClassName}
            />
          </div>

          {/* 导入进度 */}
          {confirming && (
            <div className="mt-4">
              <p className="text-gray-500 mb-2">正在导入...</p>
              <Progress percent={importProgress} status="active" />
            </div>
          )}

          {/* 底部说明 */}
          <div className="text-xs text-gray-400 mt-4">
            <p>* 映射来源为&quot;模板&quot;表示使用已知模板自动匹配，准确率较高</p>
            <p>* 映射来源为&quot;AI&quot;表示使用 AI 智能识别，建议仔细核对</p>
            <p>* 映射来源为&quot;未匹配&quot;表示该列未自动识别，请人工选择映射字段或选择&quot;不导入&quot;</p>
            <p>* 可手动调整映射字段以修正匹配不准确的情况</p>
            <p>* 如需重新分析，点击&quot;AI 分析&quot;按钮</p>
            <p className="mt-2 text-blue-600">* 列头行号和数据起始行号可根据实际 Excel 内容调整（从第1行开始计数），调整后将在导入时生效</p>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          请上传 Excel 文件以预览映射结果
        </div>
      )}
    </Modal>

      {/* 导入结果展示弹窗 */}
    <ImportResultModal
      visible={resultModalVisible}
      result={importResult}
      onClose={handleResultModalOnlyClose}
      onViewList={handleViewList}
    />
    </>
  );
};
